import { Component } from '@angular/core';
import { NgClass } from '@angular/common';
import { LucideAngularModule, CalendarClock, Sparkles, Mail, TrendingUp } from 'lucide-angular';
import {
  PAYMENTS_TAB,
  UPSELL_TAB,
  OverdueEvent,
  RiskBand,
} from './ai-events-assistant.data';

/** The two tabs that ship in this first version. */
type EventsTab = 'payments' | 'upsell';

/**
 * TVG-150: AI Events Assistant (Sales AI POC, events side).
 *
 * A static, staff-only proof-of-concept page that points the same engine at
 * live confirmed events. Everything is hard coded in
 * `ai-events-assistant.data.ts` — no backend, no API, no calculation.
 *
 * Tab 1 (Payments and risk) ranks confirmed weddings whose balance is overdue
 * by amount overdue; selecting a row opens the payment-detail panel. Tab 2
 * (Upsell) is an illustrative placeholder with two made-up examples.
 *
 * The route is gated by the `sales-ai` feature toggle (FeatureToggleGuard),
 * so the page only renders where Sales AI is enabled. Styling mirrors the
 * AI Sales Assistant page.
 */
@Component({
  selector: 'app-ai-events-assistant',
  standalone: true,
  templateUrl: './ai-events-assistant.component.html',
  styleUrl: './ai-events-assistant.component.css',
  imports: [NgClass, LucideAngularModule],
})
export class AiEventsAssistantComponent {
  protected readonly payments = PAYMENTS_TAB;
  protected readonly upsell = UPSELL_TAB;

  protected readonly CalendarClock = CalendarClock;
  protected readonly Sparkles = Sparkles;
  protected readonly Mail = Mail;
  protected readonly TrendingUp = TrendingUp;

  /** Active tab; Payments and risk is the lead tab. */
  protected activeTab: EventsTab = 'payments';

  /** The overdue event whose detail panel is open. Defaults to the top row. */
  protected selectedEvent: OverdueEvent = PAYMENTS_TAB.events[0];

  protected selectTab(tab: EventsTab): void {
    this.activeTab = tab;
  }

  protected isTab(tab: EventsTab): boolean {
    return this.activeTab === tab;
  }

  protected selectEvent(event: OverdueEvent): void {
    this.selectedEvent = event;
  }

  protected isEventSelected(event: OverdueEvent): boolean {
    return this.selectedEvent.id === event.id;
  }

  /** Band pill colour: High risk is red, Overdue is amber. */
  protected bandClasses(band: RiskBand): string {
    return band === 'high-risk'
      ? 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300'
      : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
  }
}
